export type SatelliteFaq = {
  question: string;
  answer: string;
};

export type SatelliteConfig = {
  apex: string;
  slug: string;
  name: string;
  tagline: string;
  title: string;
  description: string;
  screenEvent: string;
  harborPath: string;
  harborCta: string;
  faqs: SatelliteFaq[];
};

export const SATELLITES: Record<string, SatelliteConfig> = {
  'weekonweekoff.com': {
    apex: 'weekonweekoff.com',
    slug: 'weekonweekoff',
    name: 'Week On Week Off',
    tagline: 'Build a week-on/week-off calendar you can print or export.',
    title: 'Week On Week Off Calendar Generator',
    description:
      'Generate a week-on/week-off custody calendar. Pick the start date and exchange day, preview three months, and download an .ics file.',
    screenEvent: 'Week On Week Off Screen',
    harborPath: '/parenting-plan/week-on-week-off',
    harborCta: 'Track this schedule automatically in Harbor',
    faqs: [
      {
        question: 'What is a week-on/week-off custody schedule?',
        answer:
          'Each parent has the children for seven consecutive days, then the other parent has the next seven. Exchanges usually happen on the same weekday each week.',
      },
      {
        question: 'Can I add this calendar to my phone?',
        answer:
          'Yes. Download the .ics file and open it in Apple Calendar, Google Calendar, or Outlook. Events are all-day so they do not shift with time zones.',
      },
      {
        question: 'Who built this tool?',
        answer:
          'The team behind Harbor, a co-parenting app for schedules, expenses, and court-ready records. This page is a free standalone tool.',
      },
    ],
  },
  'custodyscheduleapp.com': {
    apex: 'custodyscheduleapp.com',
    slug: 'custodyscheduleapp',
    name: 'Custody Schedule',
    tagline: 'Preview common custody rotations and export them as a calendar.',
    title: 'Custody Schedule Builder | 2-2-3, 2-2-5-5, 3-4-4-3',
    description:
      'Build a 2-2-3, 2-2-5-5, 3-4-4-3, alternating-week, or every-weekend custody calendar. Preview a month and download an .ics file.',
    screenEvent: 'Custody Rotation Screen',
    harborPath: '/custody-schedule-app',
    harborCta: 'Track this rotation automatically in Harbor',
    faqs: [
      {
        question: 'Which custody rotation should I use?',
        answer:
          '2-2-3 and 2-2-5-5 keep kids moving more often so neither parent goes a full week without them. Alternating weeks and week-on/week-off reduce exchanges. Every-weekend is common when one parent has weekday school time.',
      },
      {
        question: 'How is the 14-day cycle calculated?',
        answer:
          'Each preset is a repeating 14-day assignment from your start date. Day 1 of the cycle is the date you pick. We use date-only math so daylight saving does not shift a day.',
      },
      {
        question: 'Who built this tool?',
        answer:
          'The team behind Harbor. This page is a free standalone schedule builder; Harbor can track the same rotation with both parents.',
      },
    ],
  },
  'coparentmoney.com': {
    apex: 'coparentmoney.com',
    slug: 'coparentmoney',
    name: 'Co-Parent Money',
    tagline: 'Split a shared expense and draft a reimbursement request.',
    title: 'Co-Parent Expense Split Calculator',
    description:
      'Split a co-parenting expense 50/50, by income, or by a custom percent. Copy a reimbursement request letter you can send to the other parent.',
    screenEvent: 'Expense Split Screen',
    harborPath: '/coparent-money',
    harborCta: 'Track shared expenses in Harbor',
    faqs: [
      {
        question: 'What split should I use?',
        answer:
          'Many orders use 50/50. Income pro-rata splits by each parent’s share of combined income. Use a custom percent if your order or agreement specifies one.',
      },
      {
        question: 'Is the letter a legal document?',
        answer:
          'No. It is a plain-language request you can copy and send. It is not legal advice and does not create a court filing.',
      },
      {
        question: 'Who built this tool?',
        answer:
          'The team behind Harbor. Harbor keeps a shared expense ledger both parents can see, with receipts and balances.',
      },
    ],
  },
  'visitationtracker.com': {
    apex: 'visitationtracker.com',
    slug: 'visitationtracker',
    name: 'Visitation Tracker',
    tagline: 'Log exchanges and document a missed handoff.',
    title: 'Printable Visitation Log and Missed-Exchange Template',
    description:
      'Print a visitation log for custody exchanges, or draft a missed-exchange note. Free standalone templates from the team behind Harbor.',
    screenEvent: 'Visitation Log Screen',
    harborPath: '/visitation-tracker',
    harborCta: 'Record GPS-verified exchanges in Harbor',
    faqs: [
      {
        question: 'What should I write in a visitation log?',
        answer:
          'Date, planned time, who arrived, whether the handoff was on time, late, or a no-show, and a short factual note. Stick to what you observed.',
      },
      {
        question: 'Is this a court form?',
        answer:
          'No. It is a printable record you can keep or share with counsel. It is not legal advice and does not replace a court-required form.',
      },
      {
        question: 'Who built this tool?',
        answer:
          'The team behind Harbor. Harbor can attach GPS check-ins to the same kind of exchange record.',
      },
    ],
  },
  'courtreadyrecords.com': {
    apex: 'courtreadyrecords.com',
    slug: 'courtreadyrecords',
    name: 'Court-Ready Records',
    tagline: 'A printable checklist for common custody and support filings.',
    title: 'Court Documentation Checklist Generator',
    description:
      'Generate a printable documentation checklist for custody modification, child-support disputes, or relocation. Not legal advice.',
    screenEvent: 'Court Checklist Screen',
    harborPath: '/court-ready-records',
    harborCta: 'Export court-ready records from Harbor',
    faqs: [
      {
        question: 'Is this legal advice?',
        answer:
          'No. The checklist is a starting list of records people often gather. Your court, judge, and attorney decide what is required.',
      },
      {
        question: 'What situations does this cover?',
        answer:
          'Custody modification, child-support disputes, and relocation. Each situation has a different document list you can print and check off.',
      },
      {
        question: 'Who built this tool?',
        answer:
          'The team behind Harbor. Harbor exports timestamped messages, expenses, and calendar records you can attach to a filing.',
      },
    ],
  },
};

export function requireSatellite(apex: string): SatelliteConfig {
  const config = SATELLITES[apex];
  if (!config) {
    throw new Error(`Unknown satellite host: ${apex}`);
  }
  return config;
}

export function satelliteCanonicalBase(apex: string): string {
  return `https://${apex}`;
}

export function harborSatelliteUrl(config: SatelliteConfig): string {
  const params = new URLSearchParams({
    utm_source: config.apex,
    utm_medium: 'satellite',
  });
  return `https://harborcoparent.com${config.harborPath}?${params.toString()}`;
}

export function harborHomeUrl(apex: string): string {
  const params = new URLSearchParams({
    utm_source: apex,
    utm_medium: 'satellite',
  });
  return `https://harborcoparent.com/?${params.toString()}`;
}
